/**
 * Buchungen (F2-Umbau, docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md, Antoines Entscheidung D2
 * 12.09.2026 + Mockup-Freigabe 13.09.2026): "Buchungen" = alle Fälle AB DER ZUSAGE — die Liste
 * filtert `GET /api/events` deshalb clientseitig auf `istBuchung()` (B0 "L7"), BEVOR die
 * Filter-Tabs greifen. Datei umbenannt aus `pages/events.tsx` (F0 hatte das bewusst noch
 * zurückgestellt). Detailseite bleibt `pages/event-detail.tsx` (Adresse `/vorgaenge/:id`).
 */
import { GRUEN_UMRISS, istVollstaendigBezahlt, zahlungsstandMeta } from "../lib/zahlungsstand";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import { useContentRefetch } from "../ui/CopilotProvider";
import type { Customer, EventRow } from "../ui/types";
import { anlassAnzeige, BUCHUNG_STATUS_REIHENFOLGE, EVENT_STATUS, formatDate, money } from "../ui/format";
import { useListSort, type SortSpalte } from "../hooks/useListSort";
import { useUrlZustand } from "../hooks/useUrlZustand";
import { oeffneGlobaleSuche } from "../lib/globale-suche";
import { ortTypChip, ortFehltHinweis } from "../lib/ort-typ";
import { vorgangPfad } from "../lib/pfade";
import { istBuchung } from "../lib/rueckweg";
import { ErinnerungSendenDialog } from "../ui/ErinnerungSendenDialog";
import CalendarPage from "./calendar";
import {
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorNote,
  Icon,
  Input,
  KeineAngabe,
  LISTEN_KOPF_ZELLE,
  ListFooter,
  ListenReiter,
  LoadingRows,
  PageHeader,
  SortierKopf,
  StatusPill,
  ZEILEN_AKTION,
  ZeilenLink,
  Zeichenzaehler,
  DateInput,
  Select,
  Textarea,
} from "../ui/components";

// F2 (B0 "L7"): die Regel „Buchung“ lebt in `lib/rueckweg.ts` (auch Breadcrumb/Seitenleiste des Vorgangs).

const FILTERS: { key: string; label: string }[] = [
  { key: "all", label: "Alle" },
  // "Zugesagt": zugesagt, aber noch nicht (vollständig) bezahlt — inkl. `payment_failed`
  // (Zahlungsversuch fehlgeschlagen, die Zusage selbst steht weiter) und `balance_due`
  // (Restforderung nach Teilerstattung).
  { key: "won", label: "Zugesagt" },
  { key: "paid", label: "Bezahlt" },
  { key: "completed", label: "Durchgeführt" },
  { key: "cancelled", label: "Abgesagt" },
];

/** Gehört eine Buchung in den Reiter? (Eine Quelle für Liste UND Reiter-Zähler.) */
function imReiter(e: EventRow, reiter: string): boolean {
  if (reiter === "all") return true;
  if (reiter === "won") return e.status === "offer_chosen" || e.status === "payment_failed" || e.status === "balance_due";
  if (reiter === "paid") return e.status === "paid" || istBezahlterCheckoutVorgang(e);
  return e.status === reiter; // "completed" | "cancelled"
}

// R2.9 (30.08.2026): Checkout-Vorgaenge (source==='checkout') bleiben absichtlich fuer immer bei
// status='offer_chosen' (Migration 135/343) - der "Bezahlt"-Tab, der sonst exakt auf
// status==='paid' prueft, wuerde sie nie zeigen, obwohl sie voll bezahlt sein koennen.
// `checkoutDeckung` kommt bereits fertig aus GET /api/events (lib/payment-coverage.ts).
function istBezahlterCheckoutVorgang(e: EventRow): boolean {
  return e.source === "checkout" && Boolean(e.checkoutDeckung?.gedeckt);
}

/**
 * F2: "Erinnerung schicken" (Glocken-Icon je Zeile) — aktiv, solange noch eine offene Handlung
 * aussteht ("Zugesagt", noch nicht bezahlt: `offer_chosen`/`payment_failed`), sonst ausgegraut.
 * Bewusste Design-Annahme (s. Abschlussbericht): der bereits bestehende Dialog
 * (`ErinnerungSendenDialog`) und sein Backend-Endpunkt (`POST /api/events/:id/offers/resend`)
 * sind an `status==='offer_sent'` gebunden (Erinnerung an ein noch unbeantwortetes Angebot) — ein
 * Zustand, der in der Buchungen-Liste per Definition (`istBuchung`) nie vorkommt. Die Karte öffnet
 * trotzdem denselben, bereits vorhandenen Dialog (keine Neuerfindung) — bei `offer_chosen`/
 * `payment_failed` zeigt er ehrlich "Keine Erinnerung möglich", solange der Endpunkt nicht auf
 * diese Zustände erweitert ist (Backend-Erweiterung ist NICHT Teil dieses Frontend-Pakets).
 */
function istMahnbar(e: EventRow): boolean {
  return e.status === "offer_chosen" || e.status === "payment_failed" || e.status === "balance_due";
}

/** Zelle einer Buchungen-Zeile (Brief § 2.3: 12/18 Innenabstand, 14 px Spaltenabstand). */
const ZELLE =
  "border-t border-[#F6F0E4] px-[7px] py-3 align-middle first:pl-[18px] last:pr-[18px] dark:border-white/[0.06]";
const SIGNIERT_ZEILE = "inline-flex items-center gap-1 text-[11px] font-medium text-[#6F675A] dark:text-[#b8ae9e]";
const GRUPPEN_KNOPF =
  "flex w-full items-center gap-1.5 px-[18px] py-2.5 text-left text-[12px] font-semibold text-on-surface-variant motion-safe:transition-colors hover:bg-[#FBF6EC] dark:text-secondary-fixed-dim dark:hover:bg-white/[0.03]";

function datumOderLeer(wert: string | null | undefined) {
  const text = formatDate(wert);
  return text === "–" ? <KeineAngabe /> : text;
}

export default function BuchungenPage() {
  const { data, loading, error, reload } = useApi<EventRow[]>("/api/events");
  // Chat-P1: event_transition/update_event per Chat -> Liste live nachladen.
  useContentRefetch("event", reload);
  const customersQ = useApi<Customer[]>("/api/customers");
  // Hinweis-Grundlage (Mimmo/Domenico via Antoine, 03.09.2026): "Direkt in Maestro als Hinweis
  // reicht" — bewusst kein Gesehen/Ungesehen-Status, nur ein 7-Tage-Zeitfenster ab `signed_at`
  // (Backend: GET /api/cost-acceptances/kuerzlich-signiert). Der Badge verschwindet von selbst.
  const kuerzlichSigniertQ = useApi<{ eventId: string; signedAt: string }[]>("/api/cost-acceptances/kuerzlich-signiert");
  const kuerzlichSigniert = useMemo(
    () => new Set((kuerzlichSigniertQ.data ?? []).map((r) => r.eventId)),
    [kuerzlichSigniertQ.data],
  );
  // Betreiber-Feedback 04.09.2026 (docs/KONZEPT-BETREIBER-UEBERSICHT-2026-09-04.md Punkt 3):
  // "ich sehe nicht wer eine Kostenübernahme bekommen hat" — dauerhafter Status, im Unterschied
  // zum 7-Tage-Hinweis oben (der bleibt für die Frisch-Markierung erhalten, beide zusammen unten).
  const signiertStatusQ = useApi<{ eventId: string; signedAt: string }[]>("/api/cost-acceptances/signiert-status");
  const kostenuebernahmeSigniert = useMemo(
    () => new Set((signiertStatusQ.data ?? []).map((r) => r.eventId)),
    [signiertStatusQ.data],
  );
  const navigate = useNavigate();
  // H4-Folge: "Angebot erstellen" von der Kunden-Vollseite oeffnet diese Drawer vorbefuellt.
  const [params, setParams] = useSearchParams();
  // CX-Audit 2026-07-17: Filter aus der URL lesbar/setzbar (?filter=won etc.) — damit koennen
  // Dashboard-KPIs gezielt in die passend gefilterte Liste verlinken und der Zustand ueberlebt
  // Reload/Zurueck-Navigation. F2: `offer_sent` ist kein gueltiger Filter-Schluessel mehr (vor-
  // Zusage-Status, gehoert zur Anfragen-Liste) — ein solcher Link faellt ueber `setFilter`s
  // Validierung unten automatisch auf "all" zurueck, statt eine leere Ansicht zu zeigen.
  // Punkt 115 (Paket 3): Reiter und Suche in der Adresse (`useUrlZustand`, `?filter=` wie bisher).
  const [zustand, setzeZustand] = useUrlZustand({ filter: "all", q: "" });
  const filter = FILTERS.some((f) => f.key === zustand.filter) ? zustand.filter : "all";
  const setFilter = (key: string) => setzeZustand({ filter: key });
  const search = zustand.q;
  const setSearch = (q: string) => setzeZustand({ q });
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [createNotiz, setCreateNotiz] = useState("");
  const prefilledCustomerId = params.get("newFor") ?? "";
  useEffect(() => {
    if (prefilledCustomerId) setCreateOpen(true);
  }, [prefilledCustomerId]);

  // F2: echte Liste/Kalender-Umschaltung OHNE Routenwechsel (vorher `<Link to="/kalender">`).
  // `pages/calendar.tsx` rendert bereits chromlos (kein eigenes <PageHeader>, s. Kopfkommentar
  // dort) — deshalb genügt es, die vorhandene Seite unveraendert einzubetten, statt ihre knapp
  // 900 Zeilen Monats-/Wochenraster-Logik zu duplizieren oder in eine geteilte Komponente zu
  // extrahieren (bewusster Scope-Schnitt, s. Abschlussbericht).
  const [ansicht, setAnsicht] = useState<"liste" | "kalender">("liste");

  const alleEvents = data ?? [];
  // F2 (B0 "L7"): DIES ist die eigentliche "Buchungen sind ab Zusage"-Grenze — alles Weitere in
  // dieser Seite (Filter-Tabs, Zaehler, Summen) arbeitet ausschliesslich auf `buchungen`.
  const buchungen = useMemo(() => alleEvents.filter(istBuchung), [alleEvents]);

  const kundenNamen = useMemo(() => new Map((customersQ.data ?? []).map((c) => [c.id, c.name])), [customersQ.data]);
  const customerName = (id: string) => kundenNamen.get(id) ?? "–";
  const kundenFirmen = useMemo(() => new Map((customersQ.data ?? []).map((c) => [c.id, c.company ?? ""])), [customersQ.data]);

  /** Punkt 115 (Paket 3, G1): einfache Suche nach Kunde, Firma, Anlass (und Vorgangsnummer). Sie
   *  wirkt VOR den Reitern — die Reiter zählen also die Suchtreffer. */
  const suchTreffer = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return buchungen;
    return buchungen.filter((e) =>
      [kundenNamen.get(e.customerId), kundenFirmen.get(e.customerId), e.occasion, e.number].some((t) => (t ?? "").toLowerCase().includes(q)),
    );
  }, [buchungen, search, kundenNamen, kundenFirmen]);
  const filterCounts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.key, suchTreffer.filter((e) => imReiter(e, f.key)).length])) as Record<string, number>,
    [suchTreffer],
  );
  const filtered = useMemo(() => suchTreffer.filter((e) => imReiter(e, filter)), [suchTreffer, filter]);

  // Listenstandard (WARTESCHLANGE Punkt 95, Brief § 3/§ 8): alle Datenspalten sortierbar, die
  // Erinnerungs-Spalte nicht. Sortiert wird, was die Zelle zeigt — Platzhalter („Event“,
  // „Ort-Typ fehlt“, „—“ bei Abgesagt) zählen als Leerwert und stehen deshalb immer am Ende.
  const spalten = useMemo<SortSpalte<EventRow>[]>(
    () => [
      { key: "kunde", label: "Kunde", type: "text", accessor: (e) => kundenNamen.get(e.customerId) },
      { key: "datum", label: "Datum", type: "date", accessor: (e) => e.eventDate },
      { key: "anlass", label: "Anlass", type: "text", accessor: (e) => anlassAnzeige(e.occasion) },
      { key: "gaeste", label: "Gäste", type: "number", accessor: (e) => e.guestCount },
      {
        key: "ort",
        label: "Ort",
        type: "text",
        accessor: (e) => {
          const chip = ortTypChip(e.locationType, e.offSiteMode);
          return e.location || (chip.fehlt ? null : chip.label);
        },
      },
      { key: "gueltig", label: "Angebot gültig bis", type: "date", accessor: (e) => e.offerValidUntil },
      { key: "zugesagt", label: "Zugesagt am", type: "date", accessor: (e) => e.wonAt },
      { key: "status", label: "Status", type: "status", accessor: (e) => e.status, statusOrder: BUCHUNG_STATUS_REIHENFOLGE },
      {
        key: "wert",
        label: "Wert",
        type: "money",
        accessor: (e) => (e.status === "cancelled" ? null : (e.mehrdeutigeBetraege?.[0] ?? e.amountTotalCents)),
      },
    ],
    [kundenNamen],
  );
  const sort = useListSort("buchungen", spalten);

  // F5 (10.09.2026, Antoine freigegebenes Mockup "Eingeklappte Durchgeführt-Ansicht") + F2
  // (13.09.2026): in der gemischten "Alle"-Ansicht draengen sich weder durchgefuehrte noch
  // abgesagte Buchungen mehr zwischen die aktiven — beide wandern in eine eigene, standardmaessig
  // eingeklappte Gruppe ans Ende der Tabelle (Muster: `ZugesagtSpalte` in AnfragenKanban.tsx).
  // Filtert der Nutzer EXPLIZIT auf "Durchgeführt"/"Abgesagt", bleibt es bei der normalen flachen
  // Darstellung (ein zusaetzliches Einklappen ergaebe dort nur eine leere/verwirrende Liste).
  const [completedOpen, setCompletedOpen] = useState(false);
  const [cancelledOpen, setCancelledOpen] = useState(false);
  const zeigeGruppenFlach = filter !== "all";
  // Listenstandard: sortiert wird INNERHALB jeder Gruppe — die eingeklappten Gruppen bleiben am
  // Ende, eine Sortierung zieht keine durchgeführte/abgesagte Buchung zwischen die aktiven.
  const aktiveZeilen = sort.sortiere(
    zeigeGruppenFlach ? filtered : filtered.filter((e) => e.status !== "completed" && e.status !== "cancelled"),
  );
  const durchgefuehrteZeilen = sort.sortiere(zeigeGruppenFlach ? [] : filtered.filter((e) => e.status === "completed"));
  const abgesagteZeilen = sort.sortiere(zeigeGruppenFlach ? [] : filtered.filter((e) => e.status === "cancelled"));

  const totalValueCents = useMemo(() => buchungen.reduce((s, e) => s + (e.amountTotalCents ?? 0), 0), [buchungen]);
  // Fußzeile (Listenstandard, Brief § 2.5): Summe der SICHTBAREN Zeilen = des aktiven Reiters.
  // Abgesagte zählen weiterhin nicht (ihre Wert-Zelle zeigt „—“) — unter „Alle“ ist das exakt der
  // frühere Wert „Bestätigt gesamt“, in den übrigen Reitern die Summe genau dieses Reiters.
  const sichtbareSummeCents = useMemo(
    () => filtered.filter((e) => e.status !== "cancelled").reduce((s, e) => s + (e.amountTotalCents ?? 0), 0),
    [filtered],
  );
  const dateRangeLabel = useMemo(() => {
    const dates = buchungen.map((e) => e.eventDate).filter((d): d is string => Boolean(d)).sort();
    if (dates.length === 0) return null;
    const first = new Date(dates[0]!).toLocaleDateString("de-DE", { month: "long" });
    const last = new Date(dates[dates.length - 1]!).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
    return first === last.split(" ")[0] ? last : `${first} bis ${last}`;
  }, [buchungen]);

  // F2: "Erinnerung schicken" öffnet den bereits bestehenden Dialog von der Vorgangsseite
  // (`ErinnerungSendenDialog`) — keine Neuerfindung, s. Kopfkommentar an `istMahnbar`.
  const [reminderFor, setReminderFor] = useState<EventRow | null>(null);

  /**
   * F2: legt künftig über `POST /api/inquiries` an (statt direkt `POST /api/events`) — sonst
   * hätte ein hier direkt angelegter Vorgang keine Anfrage-Protokollzeile und tauchte in KEINER
   * Liste auf (weder Anfragen noch — vor einer Zusage — Buchungen). Das Feld "Kunde" bleibt ein
   * Auswahlfeld bestehender Kunden (kein Freitext); die Route selbst kennt keine `customerId` und
   * findet/erstellt den Kunden über Name+E-Mail (`finde_oder_erstelle_kunden`, Migration 409) —
   * darum werden hier die Kontaktdaten des gewählten Kunden mitgeschickt, nicht seine ID. Ein neu
   * angelegter Fall hat noch keine Zusage und ist damit per Definition eine ANFRAGE — der Klick
   * führt deshalb direkt zur neuen Vorgangsseite, statt in dieser (jetzt gefilterten) Liste auf
   * ein Ergebnis zu warten, das hier nie erscheinen wird.
   */
  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const kunde = (customersQ.data ?? []).find((c) => c.id === String(form.get("customerId") ?? ""));
    setBusy(true);
    setFormError(null);
    try {
      if (!kunde) throw new Error("Bitte einen Kunden wählen.");
      const created = await apiMutate<{ eventId: string }>("/api/inquiries", "POST", {
        customerName: kunde.name,
        customerEmail: kunde.email || undefined,
        company: kunde.company || undefined,
        phone: kunde.phone || undefined,
        addressStreet: kunde.addressStreet || undefined,
        addressZip: kunde.addressZip || undefined,
        addressCity: kunde.addressCity || undefined,
        occasion: (form.get("occasion") as string) || undefined,
        eventLocation: (form.get("location") as string) || undefined,
        eventDate: form.get("eventDate") ? new Date(form.get("eventDate") as string).toISOString() : undefined,
        guests: form.get("guestCount") ? Number(form.get("guestCount")) : undefined,
        // UX-Paket 26.08.2026 Punkt 6: eine hier eingetragene Notiz erscheint als
        // Genesis-Bubble in der Posteingang-Timeline dieses Vorgangs.
        message: createNotiz.trim() || undefined,
      });
      setCreateOpen(false);
      setCreateNotiz("");
      navigate(vorgangPfad(created.eventId));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // F5/F2: eine Zeilen-Darstellung fuer alle drei Bereiche (aktive Liste + beide eingeklappten
  // Gruppen) — keine zweite, abweichende Formulierung derselben Zeile.
  function renderEventRow(e: EventRow, collapsedStyle = false) {
    const cancelled = e.status === "cancelled";
    // F5: innerhalb einer eingeklappten Gruppe zeigt das Mockup eine neutrale Pille statt des
    // sonstigen Tons ("info") und eine reduzierte Zeilen-Deckkraft — nur in dieser gemischten
    // Ansicht, nicht wenn explizit auf "Durchgeführt"/"Abgesagt" gefiltert wird (dort bleibt die
    // reguläre EVENT_STATUS-Pille aus dem flachen Pfad, s. `zeigeGruppenFlach`).
    const statusMeta = collapsedStyle ? { ...EVENT_STATUS[e.status], tone: "neutral" as const } : EVENT_STATUS[e.status];
    const mahnbar = istMahnbar(e);
    const zahlungsMeta = zahlungsstandMeta(e);
    // Mockup 19.10.2026: vollständig bezahlte Buchungen bekommen einen dünnen grünen Rahmen.
    const bezahltRahmen = !collapsedStyle && istVollstaendigBezahlt(e);
    const name = customerName(e.customerId);
    const nebenText = cancelled ? "text-secondary-fixed-dim" : "text-[#57503F] dark:text-secondary-fixed-dim";
    return (
      // CX-Audit 2026-07-17: GANZE Zeile fuehrt zum Vorgang (wie in kunden.tsx) — vorher war nur
      // der Titel-Text ein Link, der Rest der Zeile eine tote Fläche und die Liste wirkte wie eine
      // Sackgasse.
      //
      // Einheit E (`docs/PLAN-VORGANGSSEITE-UMBAU.md`, 13.09.2026): dieselbe Zeile hatte seither
      // ZWEI Mechanismen nebeneinander — ein `onClick` auf dem `<tr>` (ohne Linkziel, also kein
      // Cmd-/Strg-/Mittelklick) und einen echten `<Link>` nur auf dem Kundennamen, der sein
      // Weiterreichen per `stopPropagation` unterdruecken musste. Beide gehen jetzt in EINEM
      // Overlay-Link auf (`ZeilenLink`): die ganze Zeile ist ein echter Link, der Kundenname
      // bleibt der sichtbare Text darunter (ein zweiter `<a>` waere eine verschachtelte
      // Doppelung desselben Ziels).
      <tr
        key={e.id}
        data-vollstaendig-bezahlt={bezahltRahmen ? "true" : undefined}
        className={`group relative transition-colors hover:bg-[#FBF6EC] dark:hover:bg-white/[0.03] ${collapsedStyle ? "opacity-[0.82]" : ""} ${bezahltRahmen ? GRUEN_UMRISS : ""}`}
      >
        <td className={ZELLE}>
          <ZeilenLink to={vorgangPfad(e.id)} label={`Vorgang von ${name} öffnen`} />
          {kundenNamen.has(e.customerId) ? (
            <span
              title={name}
              className={`block truncate font-semibold group-hover:text-primary ${cancelled ? "text-secondary line-through dark:text-secondary-fixed-dim" : "text-on-background dark:text-inverse-on-surface"}`}
            >
              {name}
            </span>
          ) : (
            <KeineAngabe />
          )}
        </td>
        <td className={`${ZELLE} whitespace-nowrap tabular-nums ${nebenText}`}>{datumOderLeer(e.eventDate)}</td>
        <td className={`${ZELLE} ${nebenText}`}>
          <span className="block truncate" title={anlassAnzeige(e.occasion) ?? "Event"}>
            {anlassAnzeige(e.occasion) ?? "Event"}
          </span>
          {e.number && <p className="truncate text-[11.5px] text-on-surface-variant/70">{e.number}</p>}
        </td>
        <td className={`${ZELLE} text-right tabular-nums ${nebenText}`}>{e.guestCount ?? <KeineAngabe />}</td>
        <td className={`${ZELLE} ${cancelled ? "text-secondary-fixed-dim" : "text-on-background dark:text-inverse-on-surface"}`}>
          <div className="flex min-w-0 flex-col items-start gap-1">
            {(e.location || ortFehltHinweis(e.location, e.locationType)) && (
              <span className={`block max-w-full truncate ${e.location ? "" : "text-[#9A9080]"}`} title={e.location ?? undefined}>
                {e.location || "Ort noch offen"}
              </span>
            )}
            {/* WP2 (Auftrags-Dashboard, 08.09.2026): korrigierter Ort-Chip, offener
                Punkt P3 aus docs/KONZEPT-BETREIBER-UEBERSICHT-2026-09-04.md — jetzt
                inkl. "Ort-Typ fehlt" statt vorher stillschweigend gar nichts.
                `ortFehltHinweis` (lib/ort-typ.ts): "Im Haus" ohne Freitext ist NICHT offen — die
                Adresse steht laengst fest (tenant_settings.company_address). Konsistent mit dem
                Seitenkopf in event-detail.tsx (Antoine, 13.09.2026: "muss ueberall konsistent
                sein"). */}
            <StatusPill meta={ortTypChip(e.locationType, e.offSiteMode)} />
          </div>
        </td>
        {/* Listenstandard (23.09.2026): zeigt jetzt `events.offer_valid_until` — GET /api/events
            liefert das Feld seit jeher mit (`...e` in routes/events.ts), die Zelle schrieb bis
            dahin fest „–“ (F2-Annahme: nach der Zusage belanglos). Antoines Brief (§ 3/§ 8) führt
            sie als echte Datumsspalte mit Werten; leer bleibt sie nur, wo das Feld leer ist. */}
        <td className={`${ZELLE} whitespace-nowrap tabular-nums ${nebenText}`}>{datumOderLeer(e.offerValidUntil)}</td>
        <td className={`${ZELLE} whitespace-nowrap tabular-nums ${nebenText}`}>{datumOderLeer(e.wonAt)}</td>
        <td className={ZELLE}>
          <div className="flex flex-col items-start gap-1">
            <StatusPill meta={statusMeta} />
            {zahlungsMeta && <StatusPill meta={zahlungsMeta} testId={zahlungsMeta.tone === "warn" ? "rest-offen" : "zahlungsstand-abzeichen"} />}
            {/* Brief § 4: Zusatzhinweis als Textzeile mit Icon — keine Pille, kein Emoji. */}
            {kuerzlichSigniert.has(e.id) ? (
              <span className={SIGNIERT_ZEILE}>
                <Icon name="edit" className="text-[11px]" /> Gerade unterschrieben
              </span>
            ) : (
              kostenuebernahmeSigniert.has(e.id) && (
                <span className={SIGNIERT_ZEILE}>
                  <Icon name="receipt_long" className="text-[11px]" /> Kostenübernahme signiert
                </span>
              )
            )}
          </div>
        </td>
        <td className={`${ZELLE} text-right font-semibold tabular-nums ${cancelled ? "text-secondary-fixed-dim" : "text-on-background dark:text-inverse-on-surface"}`}>
          {cancelled ? (
            "—"
          ) : e.mehrdeutigeBetraege && e.mehrdeutigeBetraege.length > 0 ? (
            <div className="flex flex-col items-end gap-0.5">
              {e.mehrdeutigeBetraege.map((cents, i) => (
                <span key={i}>{money(cents)}</span>
              ))}
              <span className="text-[11.5px] font-normal text-on-surface-variant/70">mehrere Varianten</span>
            </div>
          ) : (
            money(e.amountTotalCents)
          )}
        </td>
        <td className={`${ZELLE} text-center`}>
          <button
            type="button"
            disabled={!mahnbar}
            aria-label={mahnbar ? `Erinnerung an ${name} schicken` : "Keine Erinnerung nötig"}
            title={mahnbar ? "Erinnerung schicken" : "Keine offene Handlung – keine Erinnerung möglich"}
            onClick={(ev) => {
              ev.stopPropagation();
              setReminderFor(e);
            }}
            className={`${ZEILEN_AKTION} grid h-[30px] w-[30px] place-items-center rounded-lg text-[#6F675A] transition-colors hover:bg-[#F2ECDF] hover:text-[#262119] disabled:cursor-not-allowed disabled:text-[#DCD3C2] disabled:hover:bg-transparent dark:text-[#b8ae9e] dark:hover:bg-white/[0.06] dark:hover:text-inverse-on-surface dark:disabled:text-white/20`}
          >
            <Icon name="notifications" className="text-[15px]" />
          </button>
        </td>
      </tr>
    );
  }

  return (
    <>
      <PageHeader
        title="Buchungen"
        subtitle={
          buchungen.length > 0
            ? `${buchungen.length} Buchung${buchungen.length === 1 ? "" : "en"}${dateRangeLabel ? ` · ${dateRangeLabel}` : ""} · ${money(totalValueCents)} Gesamtwert`
            : "Alle Fälle ab Zusage/Zahlung – von der Anfrage bis zur Durchführung."
        }
        actions={
          <>
            {/* F2: echte Umschaltung per lokalem State statt Navigation (vorher
                <Link to="/kalender">) — "Kalender" blendet die bestehende Kalenderseite anstelle
                der Liste ein, "Liste" bringt die Tabelle zurueck, kein Routenwechsel. */}
            {/* Listenstandard (Brief § 1): neutral, nie Accent — der Primär-CTA ist die einzige
                Accent-Fläche der Seite. */}
            <div className="flex items-center gap-0.5 rounded-[10px] bg-[#EAE3D5] p-[3px] dark:bg-white/[0.06]">
              {(
                [
                  { wert: "liste", label: "Liste", icon: "view_list" },
                  { wert: "kalender", label: "Kalender", icon: "calendar_month" },
                ] as const
              ).map((a) => (
                <button
                  key={a.wert}
                  type="button"
                  aria-pressed={ansicht === a.wert}
                  onClick={() => setAnsicht(a.wert)}
                  className={`inline-flex h-[30px] items-center gap-1.5 rounded-lg px-[13px] text-[12.5px] transition-colors ${
                    ansicht === a.wert
                      ? "bg-white font-semibold text-[#262119] shadow-[0_1px_3px_rgba(45,38,26,.14)] dark:bg-[#3A3228] dark:text-inverse-on-surface"
                      : "font-medium text-[#6F675A] hover:text-[#262119] dark:text-[#b8ae9e] dark:hover:text-inverse-on-surface"
                  }`}
                >
                  <span aria-hidden="true" className="inline-flex">
                    <Icon name={a.icon} className="text-[13px]" />
                  </span>
                  {a.label}
                </button>
              ))}
            </div>
            <Button type="button" onClick={() => setCreateOpen(true)}>
              <Icon name="add" className="text-[18px]" /> Vorgang anlegen
            </Button>
          </>
        }
      />

      {ansicht === "liste" && buchungen.length > 0 && (
        <div className="mb-4">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buchungen durchsuchen (Kunde, Firma, Anlass) …"
            aria-label="Buchungen durchsuchen"
            className="w-full max-w-sm"
          />
        </div>
      )}

      {ansicht === "kalender" ? (
        <Card className="overflow-hidden p-4">
          <CalendarPage />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {/* Filter-Tabs — Listenstandard (Brief § 2.1): echte Reiter-Rollen, Zähler aus den
              Suchtreffern (Punkt 115), Sortierung bleibt beim Wechsel erhalten (useListSort). */}
          <ListenReiter
            label="Buchungen nach Status"
            reiter={FILTERS.map((f) => ({ ...f, anzahl: filterCounts[f.key] ?? 0 }))}
            aktiv={filter}
            onWahl={setFilter}
          />

          {error ? (
            <ErrorNote zentriert title="Buchungen konnten nicht geladen werden" message={error} onRetry={reload} />
          ) : !loading && buchungen.length === 0 ? (
            <EmptyState icon="list" title="Noch keine Buchungen" hint="Sobald eine Kundin zusagt, erscheint die Buchung hier." />
          ) : !loading && filtered.length === 0 ? (
            // Punkt 115 (G1): die Suche bleibt im Reiter, die Leer-Anzeige nennt Treffer in den
            // anderen Reitern; „Suche aufheben“ ist ein eigener Schritt, dazu die ⌘K-Suche.
            <EmptyState
              icon={search.trim() ? "search_off" : "list"}
              title={filterCounts.all === 0 && search.trim() ? `Keine Buchung passt zu „${search.trim()}“` : "Keine Buchungen in diesem Reiter"}
              hint={
                filterCounts.all === 0
                  ? "Liegt der Fall noch vor der Zusage, steht er unter Anfragen — die Suche über alle Bereiche findet ihn."
                  : "Wechsle den Reiter oder zeige alle Buchungen an."
              }
              andereTreffer={FILTERS.filter((f) => f.key !== "all" && f.key !== filter).map((f) => ({
                label: f.label,
                anzahl: filterCounts[f.key] ?? 0,
                onClick: () => setFilter(f.key),
              }))}
              link={[
                ...(filter !== "all" && (filterCounts.all ?? 0) > 0 ? [{ label: "Alle Buchungen anzeigen", onClick: () => setFilter("all") }] : []),
                ...(search.trim() ? [{ label: "In allen Bereichen suchen", onClick: () => oeffneGlobaleSuche(search.trim()) }] : []),
                ...(search.trim() ? [{ label: "Suche aufheben", onClick: () => setSearch("") }] : []),
              ]}
            />
          ) : (
            // Listenstandard (Brief § 2.4): eigener Scrollbereich bis zur Viewport-Unterkante, damit
            // der Kopf `sticky` stehen bleibt — an der Karte selbst (`overflow-hidden`) scheiterte
            // `sticky` still. Die Fußzeile steht darunter und bleibt sichtbar.
            // ponytail: keine Virtualisierung (realistisch < 500 Zeilen); nachrüsten ab ~200 Zeilen.
            <div className="max-h-[max(20rem,calc(100vh-19rem))] overflow-auto" aria-busy={loading}>
              <table className="handy-karten w-full min-w-[1200px] table-fixed border-collapse text-left text-[13px]">
                <colgroup>
                  <col />
                  <col className="w-[110px]" />
                  <col />
                  <col className="w-[72px]" />
                  <col />
                  <col className="w-[164px]" />
                  <col className="w-[124px]" />
                  <col className="w-[182px]" />
                  <col className="w-[126px]" />
                  <col className="w-[62px]" />
                </colgroup>
                <thead>
                  <tr>
                    {spalten.map((sp) => (
                      <SortierKopf
                        key={sp.key}
                        label={sp.label}
                        aktiv={sort.sortKey === sp.key ? sort.dir : null}
                        onClick={() => sort.toggle(sp.key)}
                        rechts={sp.key === "gaeste" || sp.key === "wert"}
                      />
                    ))}
                    <th className={LISTEN_KOPF_ZELLE}>
                      <span className="sr-only">Erinnerung</span>
                    </th>
                  </tr>
                </thead>
                {loading ? (
                  <LoadingRows rows={8} spalten={["text", "text", "text", "text", "text", "text", "text", "pill", "text", "leer"]} />
                ) : (
                  <tbody>
                    {aktiveZeilen.map((e) => renderEventRow(e))}
                    {durchgefuehrteZeilen.length > 0 && (
                      <>
                        {/* F5: Trenn-/Klapp-Zeile über die volle Spaltenbreite — GENAU dasselbe
                            Muster (Icon + Anzahl rechts) wie der "Abgelehnt"-Bereich in
                            AnfragenKanban.tsx (`ZugesagtSpalte`), hier als `<tr>` statt Box, damit
                            Spaltenausrichtung/Border derselben `<table>` erhalten bleiben. */}
                        <tr>
                          <td colSpan={10} className="border-t border-[#F6F0E4] p-0 dark:border-white/[0.06]">
                            <button
                              type="button"
                              onClick={() => setCompletedOpen((v) => !v)}
                              aria-expanded={completedOpen}
                              data-testid="buchungen-durchgefuehrt-toggle"
                              className={GRUPPEN_KNOPF}
                            >
                              <Icon name={completedOpen ? "expand_less" : "expand_more"} className="text-[16px]" />
                              Durchgeführt
                              <span className="ml-auto text-on-surface-variant/70">{durchgefuehrteZeilen.length}</span>
                            </button>
                          </td>
                        </tr>
                        {completedOpen && durchgefuehrteZeilen.map((e) => renderEventRow(e, true))}
                      </>
                    )}
                    {abgesagteZeilen.length > 0 && (
                      <>
                        <tr>
                          <td colSpan={10} className="border-t border-[#F6F0E4] p-0 dark:border-white/[0.06]">
                            <button
                              type="button"
                              onClick={() => setCancelledOpen((v) => !v)}
                              aria-expanded={cancelledOpen}
                              data-testid="buchungen-abgesagt-toggle"
                              className={GRUPPEN_KNOPF}
                            >
                              <Icon name={cancelledOpen ? "expand_less" : "expand_more"} className="text-[16px]" />
                              Abgesagt
                              <span className="ml-auto text-on-surface-variant/70">{abgesagteZeilen.length}</span>
                            </button>
                          </td>
                        </tr>
                        {cancelledOpen && abgesagteZeilen.map((e) => renderEventRow(e, true))}
                      </>
                    )}
                  </tbody>
                )}
              </table>
            </div>
          )}
          {!error && (
            <ListFooter
              anzahl={filtered.length}
              gesamt={buchungen.length}
              einheit={buchungen.length === 1 ? "Buchung" : "Buchungen"}
              summe={money(sichtbareSummeCents)}
              sortiertNach={sort.spalte?.label ?? null}
              richtung={sort.dir}
              onReset={sort.reset}
            />
          )}
        </Card>
      )}

      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title="Vorgang anlegen">
        <form className="space-y-4" onSubmit={(e) => void handleCreate(e)}>
          {formError && <ErrorNote message={formError} />}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Kunde *
            </span>
            {/* A-03: key an den Ladezustand koppeln, damit defaultValue={prefilledCustomerId}
                greift, sobald die Kundenliste async nachgeladen ist (sonst bleibt die
                Vorauswahl aus ?newFor= leer und ein falscher Kunde wird riskiert). */}
            <Select
              key={customersQ.data ? "cust-ready" : "cust-loading"}
              name="customerId"
              required
              defaultValue={prefilledCustomerId}
              placeholder="– Kunde wählen –"
              options={(customersQ.data ?? []).map((cu) => ({ value: cu.id, label: `${cu.name}${cu.company ? ` (${cu.company})` : ""}` }))}
            />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Anlass
            </span>
            <Input name="occasion" placeholder="z. B. Sommerfest" />
          </label>
          {/* min-w-0: s. VeranstaltungsDatenDrawer.tsx (identisches Zwei-Felder-Muster). */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block min-w-0">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                Datum
              </span>
              <DateInput name="eventDate" />
            </label>
            <label className="block min-w-0">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                Gäste
              </span>
              <Input name="guestCount" type="number" min="0" placeholder="80" />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Ort
            </span>
            <Input name="location" placeholder="z. B. Villa Mignon, Hamburg" />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Notiz <span className="font-normal text-on-surface-variant">(optional)</span>
            </span>
            {/* UX-Paket 26.08.2026 Punkt 6: erscheint als Ursprungs-Nachricht (Genesis-Bubble) in
                der Posteingang-Timeline dieses Vorgangs - z. B. "Kundin hat telefonisch nach
                einem Termin im Oktober gefragt". */}
            <Textarea
              className="resize-y"
              rows={3}
              maxLength={5000}
              placeholder="z. B. worum es bei der Anfrage ging"
              value={createNotiz}
              onChange={(e) => setCreateNotiz(e.target.value)}
            />
            <Zeichenzaehler current={createNotiz.length} max={5000} />
          </label>
          <div className="flex justify-end gap-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setCreateOpen(false);
                setCreateNotiz("");
              }}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Speichert …" : "Vorgang anlegen"}
            </Button>
          </div>
        </form>
      </Drawer>

      <ErinnerungSendenDialog
        open={reminderFor != null}
        onClose={() => setReminderFor(null)}
        eventId={reminderFor?.id}
        eventNumber={reminderFor?.number}
        onSent={() => {
          setReminderFor(null);
          reload();
        }}
      />
    </>
  );
}
